<template>
  <div v-if="dataLoaded">
    <div class="row q-mb-lg">
      <q-btn
        class="capitalize ctrl-button"
        color="grey-5"
        :label="getString('action.toOverview')"
        @click="gotoRoute(`/${moduleContext}`)"
        icon="keyboard_backspace"
      />
    </div>
    <div class="row gutter-sm q-mb-lg">
      <!-- General Information -->
      <div class="col-md-6 col-sm-12 col-xs-12">
        <p class="card-title">
          {{ getString('module.logs.details.general.title') }}
        </p>
        <div class="card-block">
          <!-- Level -->
          <div class="row">
            <div class="col-3">
              <p class="card-data data-title">
                {{ getString('module.logs.details.general.level') }}
              </p>
            </div>
            <div class="col-9">
              <div style="display: inline-flex;">
                <div class="log-message" :class="messageClass(data.level)"></div>
                <p class="card-data data-content">{{ data.level | capitalize }}</p>
              </div>
            </div>
          </div>
          <!-- User -->
          <div class="row">
            <div class="col-3">
              <p class="card-data data-title">
                {{ getString('module.logs.details.general.user') }}
              </p>
            </div>
            <div class="col-9">
              <p class="card-data data-content">{{ data.user | checkEmpty }}</p>
            </div>
          </div>
          <!-- Message -->
          <div class="row">
            <div class="col-3">
              <p class="card-data data-title">
                {{ getString('module.logs.details.general.message') }}
              </p>
            </div>
            <div class="col-9">
              <p class="card-data data-content">{{ data.message }}</p>
            </div>
          </div>
          <!-- Timestamp -->
          <div class="row">
            <div class="col-3">
              <p class="card-data data-title">
                {{ getString('datetime.label') }}
              </p>
            </div>
            <div class="col-9">
              <p class="card-data data-content">{{ formatDatetime }}</p>
            </div>
          </div>
        </div>
      </div>
      <!-- Logged by -->
      <div class="col-md-6 col-sm-12 col-xs-12">
        <p class="card-title">
          {{ getString('module.logs.details.loggedBy.title') }}
        </p>
        <div class="card-block">
          <!-- File -->
          <div class="row">
            <div class="col-3">
              <p class="card-data data-title">
                {{ getString('module.logs.details.loggedBy.file') }}
              </p>
            </div>
            <div class="col-9">
              <p class="card-data data-content">{{ data.debug.file }}</p>
            </div>
          </div>
          <!-- Line -->
          <div class="row">
            <div class="col-3">
              <p class="card-data data-title">
                {{ getString('module.logs.details.loggedBy.line') }}
              </p>
            </div>
            <div class="col-9">
              <p class="card-data data-content">{{ data.debug.line }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row gutter-sm q-mb-lg">
      <!-- Request card -->
      <div class="col-md-6 col-sm-12 col-xs-12">
        <p class="card-title">
          {{ getString('module.logs.details.request.title') }}
        </p>
        <div class="card-block">
          <!-- Method -->
          <div class="row">
            <div class="col-3">
              <p class="card-data data-title">
                {{ getString('module.logs.details.request.method') }}
              </p>
            </div>
            <div class="col-9">
              <p class="card-data data-content">{{ data.method }}</p>
            </div>
          </div>
          <!-- Request URL -->
          <div class="row">
            <div class="col-3">
              <p class="card-data data-title">
                {{ getString('module.logs.details.request.requestUrl') }}
              </p>
            </div>
            <div class="col-9">
              <p class="card-data data-content">{{ data.request_url | checkEmpty }}</p>
            </div>
          </div>
          <!-- Source -->
          <div class="row">
            <div class="col-3">
              <p class="card-data data-title">
                {{ getString('module.logs.details.request.source') }}
              </p>
            </div>
            <div class="col-9">
              <p class="card-data data-content">{{ data.source | capitalize }}</p>
            </div>
          </div>
        </div>
      </div>
      <!-- Response card (conditional) -->
      <div class="col-md-6 col-sm-12 col-xs-12" v-if="data.answer">
        <p class="card-title">
          {{ getString('module.logs.details.answer.title') }}
        </p>
        <div class="card-block q-pa-pm" v-if="typeof data.answer === 'string'">
          {{ data.answer }}
        </div>
        <div class="card-block q-pa-pm" v-else-if="typeof data.answer === 'object'">
          <div class="row" v-for="(value, key) in data.answer" :key="key">
            <div class="col-3">
              <p class="card-data data-title">
                {{ key | capitalize }}
              </p>
            </div>
            <div class="col-9">
              <p class="card-data data-content">{{ value | checkEmpty }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row gutter-sm q-mb-lg">
      <!-- Server -->
      <div class="col-12">
        <p class="card-title">
          {{ getString('module.logs.details.server.title') }}
        </p>
        <div class="card-block">
          <!-- Item -->
          <div class="row" v-for="(value, key) in data.info" :key="key">
            <div class="col-3">
              <p class="card-data data-title">{{ key | capitalize }}</p>
            </div>
            <div class="col-9">
              <p class="card-data data-content">{{ value | checkEmpty }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

// Mixins
import fetchDetail from '@/mixins/detailViews/fetchDetail';
import getString from '@/helpers/GetStrings';
import common from '../common';

export default {
  mixins: [
    fetchDetail,
    common,
  ],
  name: 'LogDetail',
  data() {
    return {
      moduleContext: 'logs',
      moduleEndpoint: 'log',
      dataLoaded: false,
      data: null,
      logLevel: {
        message: 'message-level',
        error: 'error-level',
        warning: 'warning-level',
      },
    };
  },
  methods: {
    getString,
    gotoRoute(path) {
      this.$router.push(path);
    },
    messageClass(level) {
      return this.logLevel[level];
    },
  },
  computed: {
    // Format time (callback defined in columnsDefinition)
    formatDatetime() {
      // Unix timestamp formatted as "2018-16-05, 11:36 pm.."
      // return value > 0 ? moment(value).format('YYYY-DD-MM, h:mm a') : '-';
      return moment(this.data.datetime).format('YYYY-DD-MM, h:mm a');
    },
  },
  filters: {
    checkEmpty(value) {
      return value === null || value === '' ? '-' : value;
    },
    capitalize(value) {
      if (!value) return '';
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
.data-title
  font-weight 700

.data-content
  word-break break-all

p.card-data
  margin 0px 0 6px !important

.log-message
  width 6px
  height 6px;
  border-radius 50%;
  margin 0.225rem 0.5rem 0 0

.message-level
  background-color $positive

.error-level
  background-color $ctrl-red

.warning-level
  background-color $ctrl-yellow

</style>
